import { type JSX } from "react";
import { RichText } from "@sitecore-content-sdk/nextjs";
import { WelcomeModuleProps } from "components/welcome-module/welcome-module.props";
import Header from "../../molecules/Header/Header";
import RichTextWithTable from "../../molecules/RichTextWithTable/RichTextWithTables";
import { extractHiddenBlock } from "../../utils/styles";
import { NoDataFallback } from "../../utils/NoDataFallback";

export const Default = (props: WelcomeModuleProps): JSX.Element => {
  const { fields, params, page } = props;

  // Authors can pick the PageNotFound layout via this rendering param.
  if (params?.FieldNames === "PageNotFound") {
    return <PageNotFound {...props} />;
  }

  if (!fields) {
    return <NoDataFallback componentName="Welcome Module" />;
  }

  const isWhiteText = !!params?.Styles?.includes("text-white");

  return (
    <Header
      id={params?.RenderingIdentifier}
      title={fields.Title}
      subtitle={fields.Subtitle}
      body={fields.Body}
      image={fields.Image}
      youtubeUrl={fields.YoutubeVideoUrl}
      primaryCta={fields.PrimaryCTA}
      secondaryCta={fields.SecondaryCTA}
      whiteText={isWhiteText}
      styleProps={params?.Styles ?? ""}
      gridProps={params?.GridParameters ?? ""}
      componentName="WelcomeModule"
      isPageEditing={page?.mode?.isEditing ?? false}
      isPreviewMode={page?.mode?.isPreview ?? false}
    />
  );
};

export const PageNotFound = (props: WelcomeModuleProps): JSX.Element => {
  console.log("---->[WelcomeModule] Component", props);

  const { fields, params } = props;

  if (!fields) {
    return <NoDataFallback componentName="Welcome Module" />;
  }

  // Note: kept as strict equality (matches the original) — the Default
  // variant uses `.includes()` instead. Different by design.
  const isWhiteText = params?.Styles === "text-white";
  const id = params?.RenderingIdentifier;
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);
  const { Title, Body } = fields;

  return (
    <div
      className={`px-[var(--spacing-sixteen)] mb-[56px] pt-5 ${hiddenBlockStyles}`}
      id={id ?? undefined}
      data-testid="page-not-found-component"
      data-role="page-not-found-component"
    >
      {Title && (
        <RichText
          tag="h1"
          className={`richtext-content mt-0 mb-5 relative text-[34px] lg:text-[42px] !leading-[42.16px] lg:!leading-[52.08px] tracking-[0] font-medium pr-[var(--spacing-sixteen)] pb-[15px] after:content-[''] after:block after:clear-both after:w-[31%] after:pt-[15px] after:border-b-2 after:border-teal ${
            isWhiteText ? "text-white" : "text-ecolab-blue"
          } lg:w-[72%]`}
          field={Title}
          data-testid="title"
        />
      )}
      {Body && (
        <RichTextWithTable
          tag="p"
          className={`richtext-content z-10 pl-[2px] mb-[20px] text-base leading-[1.5] tracking-[0.4px] lg:tracking-[0.8px] m-0 font-normal pr-[var(--spacing-sixteen)] ${
            isWhiteText ? "text-white" : "text-gray"
          }`}
          field={Body}
          data-testid="body"
        />
      )}
    </div>
  );
};
